/**
 * rbacService.js — Yetki Yönetimi (backlog #6, 2026-10-08): roller, yetki kataloğu, rol yetkileri ve
 * kök kullanıcı devri.
 *
 * Base: {JAVA_BACKEND_API_URL}/rbac — FDS-Backend RbacController. WAS (until #308): HER uç YALNIZCA KÖK
 * KULLANICIYA (SuperAdmin, grup 1) açık. #308'den beri rbac.manage yetkisi isteniyor (kök her zaman
 * sahip; başka role yalnızca kök verir); Kök Devri hâlâ yalnızca kök. Backend her çağrıda veritabanından
 * kontrol eder: oturum yoksa 401, yetki yoksa 403. Mock yok — bu sayfanın sahte veriyle çalışmasının
 * anlamı yok.
 * Tek istisna getMyPermissions (GET /rbac/me, #195): her kullanıcı yalnızca KENDİ yetkilerini okur.
 *
 * Bir kullanıcıyı rolden role taşımak burada DEĞİL: userService.updateUserGroup
 * (PUT /users/{id}/group, MANAGE_USER_ROLES — kök de geçer). O uç artık kimseyi SuperAdmin yapmıyor;
 * kök yalnızca transferRoot ile el değiştirir.
 *
 * Roller ve yetkiler V69'dan beri operasyonel veri: hiçbir R__ dosyası onları TRUNCATE etmiyor, burada
 * kaydedilen kalıcı.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';

const BASE = `${JAVA_BACKEND_API_URL}/rbac`;

function parseError(error) {
  const message =
    error.response?.data?.message ||
    (typeof error.response?.data === 'string' ? error.response.data : null) ||
    error.message ||
    'Bilinmeyen hata';
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  throw wrapped;
}

/**
 * Backend'deki PermissionConstant anahtarları (permission.permission_key). Bir eylemi göstermeden önce
 * getMyPermissions().permissions içinde aranır. Yalnızca görünüm: uç kendisi de kontrol ediyor.
 */
export const PERMISSION_KEYS = {
  MANAGE_USER_ROLES: 'MANAGE_USER_ROLES',
  WORKGROUP_MANAGE: 'WORKGROUP_MANAGE',
  STAND_REQUEST_MANAGE: 'STAND_REQUEST_MANAGE',
  SYSTEM_SETTINGS_MANAGE: 'SYSTEM_SETTINGS_MANAGE',
  FIRMA_HARD_DELETE: 'FIRMA_HARD_DELETE',   // #195
  RBAC_MANAGE: 'rbac.manage',               // #308: Yetki Yönetimi sayfası (kök her zaman sahip)
  KISI_FIRMA_ILISKISI_MANAGE: 'kisi.firma_iliskisi.manage', // #81: personel tarafında kişi–firma ilişkisini sonlandırma / geri alma
  STAND_FIRMA_MANAGE: 'stand.firma.manage', // #324: firmayı standdan kaldırma / standdaki türünü değiştirme (V77: Admin)
  ONAY_METNI_MANAGE: 'onay_metni.manage',   // #253 / #536: onay metinlerini oluşturma, taslak yazma, yayınlama (V79: Admin)
  ILETISIM_KISISI_MANAGE: 'iletisim_kisisi.manage', // #537 / #538: portal Ana Sayfa "İletişim" kişilerini ekleme, düzenleme, silme (V111: Admin)
  KULLANICI_OLUSTUR: 'kullanici.olustur',   // #628: firma tarafı giriş oluşturma / firmaya bağlama / aktivasyon maili (V122: Admin, Kayıt, Saha rolleri)
};

/**
 * #325 (2026-10-11): personel SAYFALARI rol id dizileriyle değil bu izinlerle açılır (FDS-Backend V76; kapsam 1).
 * Rotalar allRoutes.js'te `permission:` taşır; menü öğeleri bağlandıkları rotanın iznini alır (VerticalLayouts).
 * V76, her rolün bugünkü sayfalarını birebir koruyacak şekilde verdi; yeni rol şablonunun sayfa izinlerini kopyalar.
 */
export const SAYFA_IZNI = {
  FUAR: 'page.fuar',                     // Fuarlar, salonlar, kroki
  FUAR_YONETIM: 'page.fuar.yonetim',     // Fuar alanları, fuar kuralları
  FIRMA: 'page.firma',
  KISI: 'page.kisi',
  STAND: 'page.stand',
  STAND_YONETIM: 'page.stand.yonetim',   // Yeni stand, tadilat
  PROJE: 'page.proje',
  PROJE_YONETIM: 'page.proje.yonetim',   // Global kurallar
  SAHA: 'page.saha',                     // Denetim, DÖF, uygunsuzluk, saha formları
  FORM_YONETIM: 'page.form.yonetim',     // Form tasarımı, şablonlar, gönderilenler
  SISTEM: 'page.sistem',                 // E-posta, görevler, dosya / firma türleri
  SISTEM_YONETIM: 'page.sistem.yonetim', // Plan, dosya ve kayıt ayarları (#199)
  KULLANICI: 'page.kullanici',
};

/**
 * #325: giriş yapan kullanıcının bu izni var mı? null = henüz bilinmiyor (GET /rbac/me bekleniyor), sonra true / false.
 * Kök her izne sahiptir. Yalnızca görünüm kararı içindir; backend her çağrıda kendisi kontrol eder.
 */
export function useYetki(izin) {
  const [var_, setVar] = useState(null);
  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const me = await getMyPermissions();
        if (!ignore) setVar(Boolean(me?.root) || (me?.permissions || []).includes(izin));
      } catch {
        if (!ignore) setVar(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [izin]);
  return var_;
}

/** #308: permission.kapsam — 1 personel rolleri için, 2 firma portalı (firma tarafı roller) için. */
export const KAPSAM = { PERSONEL: 1, PORTAL: 2 };

let _myPermissions = { userKey: null, promise: null };

/**
 * Backlog #195: GİRİŞ YAPMIŞ KULLANICININ kendi rolü ve yetkileri. GET /rbac/me, bu dosyadaki tek
 * KÖK-OLMAYAN uç: herkes yalnızca kendininkini okur.
 * → { userId, userGroupId, root, staff, permissions: ['FIRMA_HARD_DELETE', ...] } (kökte hepsi)
 * Kullanıcı başına bir kez çekilir (başka kullanıcıyla girilince yeniden); başarısız istek önbelleğe
 * alınmaz. `force` ile yenilenir.
 */
export async function getMyPermissions({ force = false } = {}) {
  const headers = getAuthHeader();
  const userKey = headers?.['X-user-Id'] ?? null;   // getAuthHeader's spelling
  if (!_myPermissions.promise || force || _myPermissions.userKey !== userKey) {
    const promise = axios
      .get(`${BASE}/me`, { headers })
      .then((response) => ({ ...response.data, permissions: response.data?.permissions || [] }))
      .catch((error) => {
        if (_myPermissions.promise === promise) _myPermissions = { userKey: null, promise: null };
        parseError(error);
      });
    _myPermissions = { userKey, promise };
  }
  return _myPermissions.promise;
}

/** [{ permissionId, permissionKey, permissionName, description }] */
export async function listPermissions() {
  try {
    const response = await axios.get(`${BASE}/permissions`, { headers: getAuthHeader() });
    return response.data || [];
  } catch (error) {
    parseError(error);
  }
}

/**
 * [{ userGroupId, userGroupName, isDenetmen, root, allPermissions, firmaSide, panelAddress,
 *    redirectUrl, activeUsers, permissionIds }]
 */
export async function listRoles() {
  try {
    const response = await axios.get(`${BASE}/groups`, { headers: getAuthHeader() });
    return response.data || [];
  } catch (error) {
    parseError(error);
  }
}

/**
 * Yeni PERSONEL rolü; şablon rolün eski sütunlarını kopyalar, yetkisiz başlar. → { userGroupId, ... }
 * #421: userGroupNameEn (isteğe bağlı) rolün İngilizce adı; yoksa İngilizce oturumda Türkçe ad görünür.
 */
// WAS (until #421): export async function createRole(userGroupName, templateGroupId) — { userGroupName, templateGroupId }
export async function createRole(userGroupName, templateGroupId, userGroupNameEn) {
  try {
    const response = await axios.post(`${BASE}/groups`, { userGroupName, templateGroupId, userGroupNameEn }, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Rol adını değiştirir. #421: userGroupNameEn verilmezse (undefined) İngilizce ad olduğu gibi kalır; boş metin İngilizce adı
 * kaldırır (İngilizce oturumda Türkçe ad görünür); dolu metin onu yazar.
 */
// WAS (until #421): export async function renameRole(groupId, userGroupName) — { userGroupName }
export async function renameRole(groupId, userGroupName, userGroupNameEn) {
  try {
    const response = await axios.put(`${BASE}/groups/${groupId}`, { userGroupName, userGroupNameEn }, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

// #408 (2026-10-14): setRolePageAccess is GONE. PUT /rbac/groups/{id}/page-access and user_group.page_access_group_id were removed from the
// backend (V94): pages open by the page.* permissions since #325. WAS, kept commented out:
// /**
//  * #302: rol, pageAccessGroupId rolünün sayfalarını ve menüsünü alır; null = kendi sayfaları (hazır
//  * roller). Kaynak bir personel rolü olmalı ve kendisi başkasından almamalı (backend 400 döner).
//  * → { userGroupId, pageAccessGroupId, effectivePageAccessGroupId, previousPageAccessGroupId }
//  */
// export async function setRolePageAccess(groupId, pageAccessGroupId) {
//   try {
//     const response = await axios.put(`${BASE}/groups/${groupId}/page-access`, { pageAccessGroupId }, { headers: getAuthHeader() });
//     return response.data;
//   } catch (error) {
//     parseError(error);
//   }
// }

/** permissionIds = rolün kaydedildikten sonraki TAM yetki kümesi ([] hepsini kaldırır). */
export async function setRolePermissions(groupId, permissionIds) {
  try {
    const response = await axios.put(`${BASE}/groups/${groupId}/permissions`, { permissionIds }, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/** → { userGroupId, total, limit, users: [UserListItemDTO] } — aktifler önce, en fazla `limit` (≤500). */
export async function listRoleMembers(groupId, { q = '', limit = 200 } = {}) {
  try {
    const params = { limit };
    if (q && q.trim()) params.q = q.trim();
    const response = await axios.get(`${BASE}/groups/${groupId}/users`, { headers: getAuthHeader(), params });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/** Kökü targetUserId'ye devreder; çağıran newGroupIdForCurrentRoot rolüne geçer (tek işlem). */
export async function transferRoot(targetUserId, newGroupIdForCurrentRoot) {
  try {
    const response = await axios.post(`${BASE}/root/transfer`, { targetUserId, newGroupIdForCurrentRoot }, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

// ── Yönetim sayfası (backlog #308 / #309, 2026-10-10) ──────────────────────────────────────
// Hepsi rbac.manage ister (kök her zaman geçer). Kök olmayan yönetici kendi rolünü / yetkilerini /
// firma bağlantısını değiştiremez, rbac.manage'i veremez — backend 403 ile döner, mesajı gösterilir.

/** Boş / null değerleri istekten çıkarır (backend "verilmedi" = filtre yok). */
function compact(params) {
  const out = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    out[k] = v;
  });
  return out;
}

/**
 * GET /rbac/users — ayrıntılı filtreli kullanıcı listesi.
 * params: { q, groupId, staff, active, firmaId, firmaVar, firmaNation, firmaTypeId (4 = tüm taşeron
 *   tipleri), fuarId, girisVar, sonGirisBaslangic, sonGirisBitis (yyyy-MM-dd), sort (ad|sonGiris|kayit|rol),
 *   page, size }
 * → { total, page, size, users: [{ userId, userLoginName, userName, userSurname, userEmail, userActive,
 *     userLastLogin, userGroupId, userGroupName, staff, root, firmalar: [{ firmaId, firmaName, yabanci,
 *     fuarRolleri, fuarRolAdlari }] }] }
 */
export async function listManagedUsers(params = {}) {
  try {
    const response = await axios.get(`${BASE}/users`, { headers: getAuthHeader(), params: compact(params) });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/** PUT /rbac/users/{id}/group — rol değiştirme; personel rolüne alma / çıkarma da burada. */
export async function setManagedUserGroup(userId, userGroupId) {
  try {
    const response = await axios.put(`${BASE}/users/${userId}/group`, { userGroupId }, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/** PUT /rbac/users/{id}/firma — firma kullanıcının TEK firması olur. → { userId, firmaIds, oncekiFirmaIds } */
export async function setUserFirma(userId, firmaId) {
  try {
    const response = await axios.put(`${BASE}/users/${userId}/firma`, { firmaId }, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/** DELETE /rbac/users/{id}/firma/{firmaId} — bir firma bağlantısını kaldırır. */
export async function removeUserFirma(userId, firmaId) {
  try {
    const response = await axios.delete(`${BASE}/users/${userId}/firma/${firmaId}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * GET /rbac/history — işlem geçmişi (rbac_islem_gecmisi), en yeni önce.
 * params: { hedefTipi (ROL|KULLANICI), hedefId, islem, yapanUserId, page, size }
 * → { total, page, size, items: [{ id, islem, islemAdi, hedefTipi, hedefId, hedefAdi, onceki, sonraki
 *     (JSON metin), aciklama, yapanAdi, yapanKullaniciAdi, islemTarihi }] }
 */
export async function listRbacHistory(params = {}) {
  try {
    const response = await axios.get(`${BASE}/history`, { headers: getAuthHeader(), params: compact(params) });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * GET /portal/matris (#310) — firma portalının rol × uyruk × modül tablosu ve hangi portal yetkisinin
 * hangi modülü açtığı. Yönetim sayfası, portal yetkilerinin ne işe yaradığını göstermek için okur.
 */
// WAS (until #411, unused): the matrix page uses portalService.getPortalMatris instead — one reader, not two.
// export async function getPortalMatrix() {
//   try {
//     const response = await axios.get(`${JAVA_BACKEND_API_URL}/portal/matris`, { headers: getAuthHeader() });
//     return response.data;
//   } catch (error) {
//     parseError(error);
//   }
// }
